/**
 * The full assortment for one market, against what the channel would predict.
 *
 * A gap list says what is missing and nothing about what is there. This says
 * both: **every** category on one shared gallons scale, the ones we are ahead
 * on included. A market reading 166% on orange juice and 6% on margarita mix is
 * a different conversation from one that is merely short — the first is a mix
 * problem, the second is a distribution problem, and a list of shortfalls alone
 * cannot tell them apart.
 *
 * Each row is a filled bar for what shipped and a dashed outline for the norm.
 * Putting the benchmark *behind* the bar rather than beside it means over and
 * under read as the bar crossing a line, which is one comparison instead of two.
 *
 * **The norm is leave-one-out** — this line's share of every market except this
 * one — so a market large enough to move the average is never benchmarked
 * against itself. Nothing is computed here; every figure arrives in the payload.
 */

const NONE = '—'

export interface AssortmentRow {
  readonly line: string
  readonly gallons: number | null
  readonly expected: number | null
  readonly vsNorm: number | null
  readonly pctOfNorm: number | null
  readonly distributors: number
  readonly hasGap: boolean
}

interface Props {
  readonly rows: readonly AssortmentRow[]
  readonly onPickLine?: (line: string) => void
}

function gal(value: number | null): string {
  return value === null ? NONE : Math.round(value).toLocaleString('en-US')
}

function signed(value: number | null): string {
  if (value === null) return NONE
  const rounded = Math.round(value)
  return `${rounded > 0 ? '+' : ''}${rounded.toLocaleString('en-US')} gal`
}

/** Compact axis ticks: 0, half, full. Enough to read a bar, not a grid. */
function ticks(scale: number): readonly number[] {
  return [0, scale / 2, scale]
}

export function AssortmentChart({ rows, onPickLine }: Props) {
  if (rows.length === 0) {
    return <div className="state">Nothing shipped into this market.</div>
  }

  // One scale across bars AND norms, or a line we are far short on would draw
  // its benchmark off the end of the track.
  const scale = Math.max(
    ...rows.map((r) => Math.max(r.gallons ?? 0, r.expected ?? 0)),
    1,
  )
  const pctOf = (value: number | null) => ((value ?? 0) / scale) * 100

  const lines = rows.length
  const total = rows.reduce((sum, r) => sum + (r.gallons ?? 0), 0)

  return (
    <div className="gs-assort">
      <p className="gs-assort-head">
        Full assortment for this metro — {lines} {lines === 1 ? 'line' : 'lines'},{' '}
        {gal(total)} gal
      </p>

      <ul className="gs-assort-key">
        <li>
          <span className="gs-swatch gs-swatch-ok" /> shipped
        </li>
        <li>
          <span className="gs-swatch gs-swatch-gap" /> flagged by A4
        </li>
        <li>
          <span className="gs-swatch gs-swatch-norm" /> normal at the channel rate
        </li>
      </ul>

      <div className="gs-assort-rows">
        {rows.map((row) => {
          const behind = (row.vsNorm ?? 0) < 0
          const label = `${signed(row.vsNorm)}${
            row.pctOfNorm === null ? '' : ` (${String(Math.round(row.pctOfNorm))}%)`
          }`
          return (
            <div className="gs-assort-row" key={row.line}>
              <div className="gs-assort-name">
                {onPickLine === undefined ? (
                  row.line
                ) : (
                  <button
                    type="button"
                    className="gs-link"
                    onClick={() => {
                      onPickLine(row.line)
                    }}
                  >
                    {row.line}
                  </button>
                )}
              </div>
              <div className="gs-assort-track">
                {/* The benchmark, drawn behind the bar so crossing it is visible. */}
                <div
                  className="gs-assort-norm"
                  style={{ width: `${String(pctOf(row.expected))}%` }}
                  title={`normal at the channel rate · ${gal(row.expected)} gal`}
                />
                <div
                  className={row.hasGap ? 'gs-assort-bar is-gap' : 'gs-assort-bar'}
                  style={{ width: `${String(pctOf(row.gallons))}%` }}
                  title={`${row.line} · ${gal(row.gallons)} gal`}
                />
                <span className={behind ? 'gs-assort-delta is-behind' : 'gs-assort-delta'}>
                  {label}
                </span>
              </div>
            </div>
          )
        })}
      </div>

      <div className="gs-assort-axis" aria-hidden="true">
        {ticks(scale).map((t) => (
          <span key={t}>{gal(t)}</span>
        ))}
      </div>
    </div>
  )
}
